import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  fetchUserAnalytics,
  fetchGlobalUserMetrics,
  fetchEngagementTrends,
  fetchUserEngagementDetails,
} from "@/api/user-analytics";
import StatsCard from "@/components/dashboard/StatsCard";
import BarChart from "@/components/dashboard/BarChart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { DateRangeFilter } from "@/components/analytics/DateRangeFilter";
import { DateRange } from "@/api/business-analytics";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart as ReBars,
  Bar as ReBar,
  XAxis as ReXAxis,
  YAxis as ReYAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer as ReResponsiveContainer,
  Legend as ReLegend,
} from "recharts";
import {
  Users,
  UserPlus,
  TrendingUp,
  BarChart2,
  Shield,
  Calendar,
  RefreshCw,
  Eye,
  BookOpen,
  Headphones,
} from "lucide-react";
import { fetchRecentSubscribers } from "@/api/dashboard";

// Helper function to format date and time
const formatDateTime = (date: Date) => {
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    }

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  };

  const timeAgo = getTimeAgo(date);

  return {
    date: formattedDate,
    time: formattedTime,
    timeAgo: timeAgo,
  };
};

const UserAnalyticsDashboard = () => {
  // Initialize with current month as default (Growth tab, but not before July 2025)
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const now = new Date();
    const minDate = new Date(2025, 6, 1); // July 1, 2025
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // Ensure the range doesn't start before July 1, 2025
    return {
      from: startOfMonth < minDate ? minDate : startOfMonth,
      to: endOfMonth,
    };
  });

  // Separate date range for engagement (default current month, but not before July 2025)
  const [engagementRange, setEngagementRange] = useState<DateRange>(() => {
    const now = new Date();
    const minDate = new Date(2025, 6, 1); // July 1, 2025
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // Ensure the range doesn't start before July 1, 2025
    return {
      from: startOfMonth < minDate ? minDate : startOfMonth,
      to: endOfMonth,
    };
  });
  const [heatmapScale, setHeatmapScale] = useState<"absolute" | "normalized">(
    "absolute"
  );
  const [activeTab, setActiveTab] = useState<
    "overview" | "growth" | "engagement"
  >("overview");

  // Fetch global user metrics (not filtered by date range)
  const { data: globalMetrics, isLoading: globalMetricsLoading } = useQuery({
    queryKey: ["globalUserMetrics"],
    queryFn: fetchGlobalUserMetrics,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    staleTime: 60 * 1000,
  });

  // Fetch user analytics data with date range
  const { data: userAnalytics, isLoading: analyticsLoading, isFetching: analyticsFetching } = useQuery({
    queryKey: ["userAnalytics", dateRange],
    queryFn: () => fetchUserAnalytics(dateRange),
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    staleTime: 60 * 1000,
  });

  // Engagement trends (views, reads, plays)
  const { data: engagementTrends, isFetching: engagementTrendsFetching } = useQuery({
    queryKey: ["engagementTrends", engagementRange],
    queryFn: () => fetchEngagementTrends(engagementRange),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });

  // Engagement details (active users, top users, heatmap) with pagination
  const [topUsersPage, setTopUsersPage] = useState<number>(1);
  const pageSize = 10;
  const { data: engagementDetails, isFetching: engagementDetailsFetching } = useQuery({
    queryKey: ["engagementDetails", engagementRange, topUsersPage, pageSize],
    queryFn: () =>
      fetchUserEngagementDetails(
        engagementRange,
        pageSize,
        (topUsersPage - 1) * pageSize
      ),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000,
  });

  // Recent subscribers for overview
  const { data: recentSubscribers } = useQuery({
    queryKey: ["recentSubscribers"],
    queryFn: fetchRecentSubscribers,
    staleTime: 60 * 1000,
  });

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
  };

  // Memoized derived values (must be before any early returns)
  const growthRate = useMemo(() => {
    if (!globalMetrics) return 0;
    // Use total registrations from global insights instead of totalUsers
    const totalRegistrations = globalMetrics.globalInsights?.totalRegistrations || 0;
    return totalRegistrations > 0
      ? Math.round((globalMetrics.newUsers / totalRegistrations) * 100)
      : 0;
  }, [globalMetrics]);

  const periodStats = useMemo(() => {
    if (
      !userAnalytics ||
      !userAnalytics.monthlySignups ||
      userAnalytics.monthlySignups.length === 0
    ) {
      return {
        peak: { month: "N/A", count: 0 },
        average: 0,
        total: 0,
      };
    }
    const peak = userAnalytics.monthlySignups.reduce(
      (max, curr) => (curr.count > max.count ? curr : max),
      userAnalytics.monthlySignups[0]
    );
    const total = userAnalytics.monthlySignups.reduce(
      (sum, m) => sum + m.count,
      0
    );
    const average = Math.round(
      total / Math.max(userAnalytics.monthlySignups.length, 1)
    );
    return { peak, average, total };
  }, [userAnalytics]);

  const chartTitle = useMemo(() => {
    const from = dateRange.from;
    const to = dateRange.to;
    const isSameYear = from.getFullYear() === to.getFullYear();
    const isSameMonth = isSameYear && from.getMonth() === to.getMonth();
    const isSameDay = isSameMonth && from.getDate() === to.getDate();
    if (isSameDay) return `User Registrations - ${from.toLocaleDateString()}`;
    if (isSameMonth)
      return `Daily User Registrations - ${from.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      })}`;
    if (isSameYear) return `Monthly User Registrations - ${from.getFullYear()}`;
    return `User Registrations (${from.toLocaleDateString()} - ${to.toLocaleDateString()})`;
  }, [dateRange]);

  // Engagement derived data
  const engagementSeries = useMemo(
    () => engagementTrends?.series || [],
    [engagementTrends]
  );
  const engagementTotals = useMemo(() => {
    return engagementSeries.reduce(
      (
        acc: { views: number; reads: number; plays: number },
        curr: { views: number; reads: number; plays: number }
      ) => ({
        views: acc.views + (curr.views || 0),
        reads: acc.reads + (curr.reads || 0),
        plays: acc.plays + (curr.plays || 0),
      }),
      { views: 0, reads: 0, plays: 0 }
    );
  }, [engagementSeries]);

  const MultiBarChart = ({ title }: { title: string }) => (
    <Card className="col-span-2">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ReResponsiveContainer width="100%" height="100%">
            <ReBars
              data={engagementSeries}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <ReXAxis dataKey="month" />
              <ReYAxis />
              <ReTooltip />
              <ReLegend />
              <ReBar
                dataKey="views"
                name="Views"
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
              />
              <ReBar
                dataKey="reads"
                name="Reads"
                fill="#10b981"
                radius={[4, 4, 0, 0]}
              />
              <ReBar
                dataKey="plays"
                name="Plays"
                fill="#f59e0b"
                radius={[4, 4, 0, 0]}
              />
            </ReBars>
          </ReResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );

  const RecentSubscribersCard = () => (
    <Card className="border border-border">
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center">
          <UserPlus className="h-4 w-4 mr-2 text-primary" />
          Recent Subscribers
        </CardTitle>
        <p className="text-sm text-muted-foreground">Last 5 new subscribers</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {(recentSubscribers || []).length > 0 ? (
            (recentSubscribers || []).map(
              (s: {
                id: string | number;
                name: string;
                email: string;
                plan?: string | null;
                subscriptionDate: string;
                avatarUrl?: string | null;
              }) => {
                const t = formatDateTime(new Date(s.subscriptionDate));
                return (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg border border-border"
                  >
                    {s.avatarUrl ? (
                      <img
                        src={s.avatarUrl}
                        alt={s.name}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                        {String((s.name || "U").substring(0, 1)).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm truncate">{s.name}</h3>
                      <p className="text-xs text-muted-foreground truncate">
                        {s.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs whitespace-nowrap">
                      {s.plan && <Badge variant="secondary">{s.plan}</Badge>}
                      <div className="text-muted-foreground text-right">
                        <div>{`${t.date}, ${t.time}`}</div>
                        <div className="text-xs opacity-80">{t.timeAgo}</div>
                      </div>
                    </div>
                  </div>
                );
              }
            )
          ) : (
            <div className="text-center text-muted-foreground py-4">
              No recent subscribers
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (globalMetricsLoading || analyticsLoading) {
    return (
      <AdminLayout title="User Analytics">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!globalMetrics || !userAnalytics) {
    return (
      <AdminLayout title="User Analytics">
        <div className="p-8 text-center">
          <h2 className="text-xl font-medium mb-2">
            Unable to load user analytics
          </h2>
          <p className="text-muted-foreground">
            Please try refreshing the page.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="User Analytics">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">User Analytics</h1>
            <p className="text-muted-foreground">
              Insights into user growth, registrations, and engagement
            </p>
          </div>
          <div className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-primary" />
            <span className="text-sm text-muted-foreground">
              Last updated: {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as typeof activeTab)}
          className="space-y-6"
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="growth">Growth</TabsTrigger>
            <TabsTrigger value="engagement">Engagement</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {/* Global User Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatsCard
                title="New Users (30 days)"
                value={globalMetrics.newUsers}
                icon={<UserPlus className="h-4 w-4" />}
                footer={
                  <div className="text-xs text-muted-foreground mt-2">
                    <span className="font-medium text-foreground">
                      {growthRate}%
                    </span>{" "}
                    growth rate
                  </div>
                }
              />

              <StatsCard
                title="Growth Rate"
                value={`${growthRate}%`}
                icon={<TrendingUp className="h-4 w-4" />}
                footer={
                  <div className="text-xs text-muted-foreground mt-2">
                    New users vs total
                  </div>
                }
              />

              <StatsCard
                title="Active Subscribers"
                value={globalMetrics.activeSubscribers || 0}
                icon={<TrendingUp className="h-4 w-4" />}
                footer={
                  <div className="text-xs text-muted-foreground mt-2">
                    Total (all statuses): {globalMetrics.totalSubscribers || 0}
                  </div>
                }
              />

              <StatsCard
                title="User Roles"
                value={globalMetrics.userRoles.length}
                icon={<Shield className="h-4 w-4" />}
                footer={
                  <div className="text-xs text-muted-foreground mt-2">
                    Available role types
                  </div>
                }
              />
            </div>

            {/* Global Registration Insights */}
            <Card className="border border-border mt-6">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-primary" />
                  Global Registration Insights
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  All-time registration statistics
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {globalMetrics.globalInsights?.peakMonth || "N/A"}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Peak Registration Month
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {globalMetrics.globalInsights?.peakCount || 0} users
                    </div>
                  </div>

                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {globalMetrics.globalInsights?.averageMonthly || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Average Monthly Signups
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      All time
                    </div>
                  </div>

                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {globalMetrics.globalInsights?.totalRegistrations || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Registrations
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      All time
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* User Roles and Age Groups row */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Simplified User Roles */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Shield className="h-4 w-4 mr-2 text-primary" />
                    User Roles
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {globalMetrics.userRoles.reduce(
                      (total, role) => total + (role.userCount || 0),
                      0
                    )}{" "}
                    users across {globalMetrics.userRoles.length} roles
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(globalMetrics.userRoles || []).map((role) => (
                      <div
                        key={role.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <Shield className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <span className="text-sm truncate">{role.name}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {role.userCount || 0}
                        </Badge>
                      </div>
                    ))}
                    {(!globalMetrics.userRoles ||
                      globalMetrics.userRoles.length === 0) && (
                      <div className="text-center text-muted-foreground py-2">
                        No roles configured
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Age Groups */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-primary" />
                    Age Groups
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Distribution by profile age group
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(globalMetrics.ageGroups || []).map(
                      (ag: { name: string; count: number }) => (
                        <div
                          key={ag.name}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm truncate">{ag.name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {ag.count}
                          </Badge>
                        </div>
                      )
                    )}
                    {(!globalMetrics.ageGroups ||
                      globalMetrics.ageGroups.length === 0) && (
                      <div className="text-center text-muted-foreground py-2">
                        No data
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Below row: Recent Registrations and Recent Subscribers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Recent Registrations */}
              <Card className="border border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <UserPlus className="h-4 w-4 mr-2 text-primary" />
                    Recent Registrations
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Last 5 recent user registrations
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {userAnalytics.recentUsers.length > 0 ? (
                      userAnalytics.recentUsers.map(
                        (user: {
                          id: string | number;
                          name: string;
                          email: string;
                          registeredAt: string;
                          avatarUrl?: string | null;
                        }) => {
                          const t = formatDateTime(new Date(user.registeredAt));
                          return (
                            <div
                              key={user.id}
                              className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg border border-border"
                            >
                              {user.avatarUrl ? (
                                <img
                                  src={user.avatarUrl}
                                  alt={user.name}
                                  className="h-8 w-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                                  {user.name.substring(0, 1).toUpperCase()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-sm truncate">
                                  {user.name}
                                </h3>
                                <p className="text-xs text-muted-foreground truncate">
                                  {user.email}
                                </p>
                              </div>
                              <div className="text-xs text-muted-foreground whitespace-nowrap text-right">
                                <div>{`${t.date}, ${t.time}`}</div>
                                <div className="text-xs opacity-80">
                                  {t.timeAgo}
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )
                    ) : (
                      <div className="text-center text-muted-foreground py-4">
                        No recent users
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Subscribers */}
              <RecentSubscribersCard />
            </div>
          </TabsContent>

          <TabsContent value="growth">
            <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg border relative">
              {analyticsFetching && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Loading...</span>
                  </div>
                </div>
              )}
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={(range) => {
                  setDateRange(range);
                  setActiveTab("growth");
                }}
              />
            </div>

            <Card className="border border-border mt-4">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2 text-primary" />
                  User Growth Trend
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  📈 Filtered data based on selected date range
                </p>
              </CardHeader>
              <CardContent>
                {analyticsFetching ? (
                  <Skeleton className="h-[320px] w-full" />
                ) : (
                <div className="h-[320px]">
                  <ReResponsiveContainer width="100%" height="100%">
                    <ReBars
                      data={(userAnalytics.monthlySignups || []).map(
                        (d: { month: string; count: number }, i: number) => ({
                          month: d.month,
                          signups: d.count,
                          newSubscribers:
                            userAnalytics.monthlyNewSubscribers?.[i]?.count ||
                            0,
                        })
                      )}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <ReXAxis dataKey="month" />
                      <ReYAxis />
                      <ReTooltip />
                      <ReLegend />
                      <ReBar
                        dataKey="signups"
                        name="Signups"
                        fill="#3b82f6"
                        radius={[4, 4, 0, 0]}
                      />
                      <ReBar
                        dataKey="newSubscribers"
                        name="New Subscribers"
                        fill="#a855f7"
                        radius={[4, 4, 0, 0]}
                      />
                    </ReBars>
                  </ReResponsiveContainer>
                </div>
                )}
              </CardContent>
            </Card>

            {!analyticsFetching && (
            <Card className="border border-border mt-6">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center">
                  <TrendingUp className="h-4 w-4 mr-2 text-primary" />
                  Period Insights
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Statistics for {dateRange.from.toLocaleDateString()} to{" "}
                  {dateRange.to.toLocaleDateString()}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {periodStats.peak.month}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Peak Registration Period
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {periodStats.peak.count} users
                    </div>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {periodStats.average}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Average Per Period
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Selected range
                    </div>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary">
                      {periodStats.total}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Registrations
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Selected range
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          <TabsContent value="engagement">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">
                  Engagement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-4 p-4 bg-muted/50 rounded-lg border mb-4 relative">
                  {(engagementTrendsFetching || engagementDetailsFetching) && (
                    <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Loading...</span>
                      </div>
                    </div>
                  )}
                  <DateRangeFilter
                    dateRange={engagementRange}
                    onDateRangeChange={(range) => {
                      setEngagementRange(range);
                      setActiveTab("engagement");
                    }}
                  />
                </div>
                {(engagementTrendsFetching || engagementDetailsFetching) ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <Skeleton className="h-20" />
                      <Skeleton className="h-20" />
                      <Skeleton className="h-20" />
                    </div>
                    <Skeleton className="h-[300px] w-full" />
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                    </div>
                  </div>
                ) : (
                <>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="bg-muted/40 rounded p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      <Eye className="h-4 w-4 text-muted-foreground" /> Views
                    </div>
                    <div className="text-xl mt-1 font-bold">
                      {engagementTotals.views.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-muted/40 rounded p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      <BookOpen className="h-4 w-4 text-muted-foreground" />{" "}
                      Reads
                    </div>
                    <div className="text-xl mt-1 font-bold">
                      {engagementTotals.reads.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-muted/40 rounded p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      <Headphones className="h-4 w-4 text-muted-foreground" />{" "}
                      Plays
                    </div>
                    <div className="text-xl mt-1 font-bold">
                      {engagementTotals.plays.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <MultiBarChart title="Views, Reads, Plays (Grouped by Period)" />
                </div>

                {/* Active users metric cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                  <Card className="border border-border">
                    <CardContent className="p-4 text-center">
                      <div className="text-xs text-muted-foreground">
                        Active Users
                      </div>
                      <div className="text-xl mt-1 font-bold">
                        {engagementDetails?.activeUsers?.totalUnique?.toLocaleString?.() ||
                          0}
                      </div>
                      {typeof engagementDetails?.activeUsers?.percentChange ===
                        "number" && (
                        <div className="text-xs mt-1">
                          <span
                            className={
                              (engagementDetails.activeUsers.percentChange ||
                                0) >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            }
                          >
                            {engagementDetails.activeUsers.percentChange}%
                          </span>{" "}
                          vs prev period
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <Card className="border border-border">
                    <CardContent className="p-4 text-center">
                      <div className="text-xs text-muted-foreground">
                        Unique Viewers
                      </div>
                      <div className="text-xl mt-1 font-bold">
                        {engagementDetails?.activeUsers?.uniqueViewers?.toLocaleString?.() ||
                          0}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-border">
                    <CardContent className="p-4 text-center">
                      <div className="text-xs text-muted-foreground">
                        Unique Readers
                      </div>
                      <div className="text-xl mt-1 font-bold">
                        {engagementDetails?.activeUsers?.uniqueReaders?.toLocaleString?.() ||
                          0}
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border border-border">
                    <CardContent className="p-4 text-center">
                      <div className="text-xs text-muted-foreground">
                        Unique Listeners
                      </div>
                      <div className="text-xl mt-1 font-bold">
                        {engagementDetails?.activeUsers?.uniqueListeners?.toLocaleString?.() ||
                          0}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Active users trend line (using BarChart for consistency) */}
                <div className="mt-6">
                  <BarChart
                    title="Active Users Trend"
                    data={engagementDetails?.uniqueActiveUsersSeries || []}
                    color="hsl(262, 83%, 58%)"
                    compact={false}
                  />
                </div>

                {/* Heatmap */}
                <div className="mt-6">
                  <Card className="border border-border">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg font-semibold">
                          Activity Heatmap (Day ⨯ Hour)
                        </CardTitle>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">
                            Heatmap:
                          </span>
                          <div className="inline-flex rounded border">
                            <button
                              className={`px-2 py-1 ${
                                heatmapScale === "absolute" ? "bg-muted" : ""
                              }`}
                              onClick={() => setHeatmapScale("absolute")}
                            >
                              Absolute
                            </button>
                            <button
                              className={`px-2 py-1 ${
                                heatmapScale === "normalized" ? "bg-muted" : ""
                              }`}
                              onClick={() => setHeatmapScale("normalized")}
                            >
                              Normalized
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-auto">
                        <div
                          className="grid"
                          style={{
                            gridTemplateColumns: `repeat(25, minmax(0, 1fr))`,
                          }}
                        >
                          {/* Corner */}
                          <div></div>
                          {Array.from({ length: 24 }, (_, h) => (
                            <div
                              key={`h-${h}`}
                              className="text-[10px] text-muted-foreground text-center px-1"
                            >
                              {h}
                            </div>
                          ))}
                          {(() => {
                            const days = [
                              "Sun",
                              "Mon",
                              "Tue",
                              "Wed",
                              "Thu",
                              "Fri",
                              "Sat",
                            ];
                            const heat =
                              engagementDetails?.heatmap ||
                              Array.from({ length: 7 }, () =>
                                Array(24).fill(0)
                              );
                            const globalMax =
                              heat.reduce(
                                (m: number, row: number[]) =>
                                  Math.max(m, ...row),
                                0
                              ) || 1;
                            return days.flatMap((d, i) => [
                              <div
                                key={`d-label-${i}`}
                                className="text-[10px] text-muted-foreground text-right pr-1 py-0.5"
                              >
                                {d}
                              </div>,
                              ...heat[i].map((val: number, hIdx: number) => {
                                const rowMax = Math.max(...heat[i]);
                                const denom =
                                  heatmapScale === "normalized"
                                    ? rowMax || 1
                                    : globalMax;
                                const intensity = val / denom;
                                const bg = `rgba(59, 130, 246, ${Math.max(
                                  0.08,
                                  intensity
                                )})`;
                                return (
                                  <div
                                    key={`cell-${i}-${hIdx}`}
                                    className="h-4 w-4 m-[2px] rounded-sm"
                                    style={{
                                      backgroundColor:
                                        val === 0 ? "rgba(0,0,0,0.05)" : bg,
                                    }}
                                    title={`${d} ${hIdx}:00 — ${val} events`}
                                  />
                                );
                              }),
                            ]);
                          })()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top engaged users with pagination (last section) */}
                <div className="mt-6">
                  <Card className="border border-border">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold">
                        Top Engaged Users
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {(engagementDetails?.topUsers || []).map((u) => (
                          <div
                            key={String(u.id)}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2 truncate pr-2 max-w-[60%]">
                              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                                {String(u.name || "U")
                                  .substring(0, 1)
                                  .toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">
                                  {u.name}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {u.email}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                              {u.subscriptionStatus && (
                                <Badge
                                  variant={
                                    u.subscriptionStatus === "Expired"
                                      ? "destructive"
                                      : u.subscriptionStatus === "Cancelled"
                                      ? "secondary"
                                      : u.subscriptionStatus === "No Plan"
                                      ? "outline"
                                      : "default"
                                  }
                                >
                                  {u.subscriptionStatus === "Active"
                                    ? "Premium"
                                    : u.subscriptionStatus}
                                </Badge>
                              )}
                              <Badge variant="secondary">{u.views} views</Badge>
                              <Badge variant="secondary">{u.reads} reads</Badge>
                              <Badge variant="secondary">{u.plays} plays</Badge>
                              <Badge variant="outline">{u.total} total</Badge>
                              <span className="text-muted-foreground">
                                {u.lastReadAt
                                  ? new Date(u.lastReadAt).toLocaleString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                      }
                                    )
                                  : "—"}
                              </span>
                            </div>
                          </div>
                        ))}
                        {(!engagementDetails ||
                          (engagementDetails.topUsers || []).length === 0) && (
                          <div className="text-center text-muted-foreground py-4">
                            No engagement yet for this period
                          </div>
                        )}
                      </div>

                      {/* Pagination */}
                      {engagementDetails &&
                        (engagementDetails.topUsersTotal || 0) > pageSize && (
                          <div className="flex justify-between items-center mt-4 text-sm">
                            <button
                              className="px-3 py-1 rounded border disabled:opacity-50"
                              onClick={() =>
                                setTopUsersPage((p) => Math.max(1, p - 1))
                              }
                              disabled={topUsersPage <= 1}
                            >
                              Previous
                            </button>
                            <div className="text-muted-foreground">
                              Page {topUsersPage} of{" "}
                              {Math.max(
                                1,
                                Math.ceil(
                                  (engagementDetails.topUsersTotal || 0) /
                                    pageSize
                                )
                              )}
                            </div>
                            <button
                              className="px-3 py-1 rounded border disabled:opacity-50"
                              onClick={() => setTopUsersPage((p) => p + 1)}
                              disabled={
                                topUsersPage >=
                                Math.ceil(
                                  (engagementDetails.topUsersTotal || 0) /
                                    pageSize
                                )
                              }
                            >
                              Next
                            </button>
                          </div>
                        )}
                    </CardContent>
                  </Card>
                </div>
                </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default UserAnalyticsDashboard;
